// Settings for the Ad Copy Studio — the winners-grounded copy generator.
// Static-creative host first; the module is host-adapted so the video editor
// can adopt it once proven (reserved {{script}} variable).
//
// Same architecture as the video hook generator: editable template with
// variables, per-browser cache, team default in the generic dashboard
// settings store, shared model registry.

import { MODEL_OPTIONS, DEFAULT_GENERATION_MODEL, coerceModelOption } from 'pages/VideoCreative/models';

export { MODEL_OPTIONS, DEFAULT_GENERATION_MODEL, coerceModelOption };

export const STUDIO_SETTINGS_NAMESPACE = 'ad-copy-studio';
export const STUDIO_PROMPT_KEY = 'prompt';
export const STUDIO_PARAMS_KEY = 'params';

// v9: rule blocks moved into toggleable/editable cards ({{headline_rules}},
// {{style_library}}, {{format_rules}}) and compressed ~50% — the merged
// meta-ad-copy content was ~900 static tokens inline.
export const STUDIO_PROMPT_STORAGE_KEY = 'ad-copy-studio-prompt-v9';
export const STUDIO_HEADLINE_RULES_STORAGE_KEY = 'ad-copy-studio-headline-rules-v1';
export const STUDIO_FORMAT_RULES_STORAGE_KEY = 'ad-copy-studio-format-rules-v1';
export const STUDIO_HEADLINE_RULES_KEY = 'headlineRules';
export const STUDIO_FORMAT_RULES_KEY = 'formatRules';
// v4: rule cards + caption-style picker join the params.
export const STUDIO_PARAMS_STORAGE_KEY = 'ad-copy-studio-params-v4';
export const STUDIO_MODEL_STORAGE_KEY = 'ad-copy-studio-model';

// Tunable parameters. All refine either how many options come back or which
// winners feed the prompt.
export interface StudioParams {
  modeledCount: number;   // options modeled closely on winners
  novelCount: number;     // genuinely new angles
  lookbackDays: number;   // winners window
  metric: 'true_roas' | 'meta_roas' | 'spend';
  minSpend: number;       // winners noise floor ($)
  winnersPerType: number; // top-N headlines and top-N primary texts fetched
  /** 'top' = the absolute best N by `metric`; 'sample' = a random N of the
   * above-floor rows in the highest-True-ROAS pool the server returns (the
   * pool is ranked on the same measure the floor reads, so nothing above the
   * floor is cut before a lower-ROAS row is). Different winners each draw, so
   * the model gets varied grounding. */
  winnersMode: 'top' | 'sample';
  roasFloor: number;      // sample mode: minimum ROAS to qualify
  // Ingredient cards enabled by default (keyed by ingredient id).
  enabled: Record<string, boolean>;
  /** Caption styles in play (keyed by STYLE_LIBRARY key). Fewer checked =
   * fewer prompt tokens AND a directed format mix. */
  styles: Record<string, boolean>;
}

export const DEFAULT_PARAMS: StudioParams = {
  modeledCount: 3,
  novelCount: 2,
  lookbackDays: 30,
  metric: 'true_roas',
  minSpend: 500,
  winnersPerType: 3,
  winnersMode: 'sample',
  roasFloor: 2,
  enabled: {
    winning_headlines: true,
    winning_primary_texts: true,
    original: true,
    analysis: true,
    selling_points: false,
    extra: false,
    previous_copy: true,
    headline_rules: true,
    style_library: true,
    format_rules: true,
  },
  styles: {
    bullet_stack: true,
    prose: true,
    friendly_story: true,
    dense_benefits: true,
    compact: true,
  },
};

export function loadParams(): StudioParams {
  try {
    const raw = localStorage.getItem(STUDIO_PARAMS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PARAMS, enabled: { ...DEFAULT_PARAMS.enabled } };
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_PARAMS,
      ...parsed,
      enabled: { ...DEFAULT_PARAMS.enabled, ...(parsed.enabled || {}) },
      styles: { ...DEFAULT_PARAMS.styles, ...(parsed.styles || {}) },
    };
  } catch {
    return { ...DEFAULT_PARAMS, enabled: { ...DEFAULT_PARAMS.enabled } };
  }
}

export function saveParams(params: StudioParams): void {
  try { localStorage.setItem(STUDIO_PARAMS_STORAGE_KEY, JSON.stringify(params)); } catch { /* private mode */ }
}

/** Every toggleable section, grouped for the ingredient tab rail and the
 * section-defaults settings panel. ids match params.enabled keys. */
export type StudioSectionGroup = 'creative' | 'winners' | 'history' | 'rules';
export const STUDIO_SECTIONS: Array<{ id: string; label: string; group: StudioSectionGroup }> = [
  { id: 'original', label: "Original ad's copy", group: 'creative' },
  { id: 'analysis', label: 'Why the original worked', group: 'creative' },
  { id: 'selling_points', label: 'Selling points', group: 'creative' },
  { id: 'winning_headlines', label: 'Winning headlines', group: 'winners' },
  { id: 'winning_primary_texts', label: 'Winning primary texts', group: 'winners' },
  { id: 'previous_copy', label: 'Previously generated (anti-repetition)', group: 'history' },
  { id: 'extra', label: 'Extra instructions', group: 'rules' },
  { id: 'headline_rules', label: 'Headline rules', group: 'rules' },
  { id: 'style_library', label: 'Caption styles', group: 'rules' },
  { id: 'format_rules', label: 'Caption format rules', group: 'rules' },
];
export const STUDIO_SECTION_GROUPS: Array<{ key: StudioSectionGroup; label: string }> = [
  { key: 'creative', label: 'Creative' },
  { key: 'winners', label: 'Winners' },
  { key: 'history', label: 'History' },
  { key: 'rules', label: 'Rules' },
];

// Template variables. Section variables resolve to '' when their ingredient
// card is off — the template prunes cleanly around them.
export const VAR_WINNING_HEADLINES = '{{winning_headlines}}';
export const VAR_WINNING_PRIMARY_TEXTS = '{{winning_primary_texts}}';
export const VAR_ORIGINAL_HEADLINE = '{{original_headline}}';
export const VAR_ORIGINAL_PRIMARY_TEXT = '{{original_primary_text}}';
export const VAR_MODELED_COUNT = '{{modeled_count}}';
export const VAR_NOVEL_COUNT = '{{novel_count}}';
export const VAR_EXTRA = '{{extra_instructions}}';
export const VAR_PRODUCT = '{{product}}';
export const VAR_SELLING_POINTS = '{{selling_points}}';
export const VAR_ANALYSIS = '{{analysis}}';
export const VAR_PREVIOUS_COPY = '{{previous_copy}}';
export const VAR_HEADLINE_RULES = '{{headline_rules}}';
export const VAR_STYLE_LIBRARY = '{{style_library}}';
export const VAR_FORMAT_RULES = '{{format_rules}}';

// The rule blocks are CARDS, not baked-in prose: toggleable, editable, and
// token-gauged like every other ingredient. Defaults are deliberately terse.
export const DEFAULT_HEADLINE_RULES = `HEADLINE RULES:
- Brand voice; never I/My/He/She customer perspective — write brand claims or instructions.
- One sentence, 40 chars or less, Title Case, no split punctuation.
- Hook in the first 4-5 words; a DIFFERENT angle per headline.
- Test: works on a billboard? Yelp-review vibe = rewrite.`;

export const DEFAULT_FORMAT_RULES = `CAPTION FORMAT:
- Real line breaks: 1-3 sentence paragraphs, blank lines between, one bullet per line — no walls of text.
- Hook in the first two lines (~125-char truncation). 12 emoji max, used as punctuation.
- Say "Tap Shop Now" etc., never "click the link". No image-specific language. ONE selling point per caption.
- No cure claims, guarantees, or medical statements; no clickbait; no ALL-CAPS.`;

/** The five caption formats (from the team's meta-ad-copy skill), as data so
 * the card can offer per-style checkboxes and only checked styles spend
 * prompt tokens. */
export const STYLE_LIBRARY: Array<{ key: string; label: string; line: string }> = [
  { key: 'bullet_stack', label: 'Bullet Stack', line: 'Bullet Stack (90-180w): hook → problem → intro → ✅/💪 bullets → proof → offer → 👉 CTA.' },
  { key: 'prose', label: 'Prose Conversational', line: 'Prose Conversational (55-110w): 1-2 short paragraphs, no bullets, one emoji offer line; skeptic / sensory / casual-testimonial (only first-person allowed).' },
  { key: 'friendly_story', label: 'Friendly Story', line: 'Friendly Story (80-130w): 👉 opener, warm voice, 3-4 themed-emoji bullets (🌱 not ✅), soft ❤️ close.' },
  { key: 'dense_benefits', label: 'Dense Benefit Stack', line: 'Dense Benefit Stack (50-100w): one-line hook + category emoji → 5-8 ✅ bullets (🚫 accents) → sharp CTA.' },
  { key: 'compact', label: 'Compact Intro', line: 'Compact Intro (25-55w): intro line → 2-3 • bullets → one value+offer close.' },
];

/** The closed "angle" vocabulary — must match the list named in the OUTPUT
 * section of the template, since the model tags every option with one. */
export const ANGLE_OPTIONS = [
  'Problem-Naming', 'Promise', 'Pattern Interrupt', 'Social Proof', 'Authority',
  'Offer-Led', 'Sensory', 'Skeptic', 'Founder', 'Lifestyle',
];

/** Pairings that fight the styles' own rules — the rotation never draws them.
 * Founder speaks in the first person, which only Prose Conversational
 * permits; Skeptic needs a doubt-then-turn arc and Sensory/Lifestyle need
 * scene-painting room that the compressed bullet formats don't give. */
export const STYLE_ANGLE_CONFLICTS: Record<string, string[]> = {
  'Bullet Stack': ['Founder'],
  'Prose Conversational': [],
  'Friendly Story': ['Founder'],
  'Dense Benefit Stack': ['Founder', 'Skeptic', 'Sensory', 'Lifestyle'],
  'Compact Intro': ['Founder', 'Skeptic', 'Sensory'],
};

/** Headlines are brand voice (no I/My per the headline rules), so a Founder
 * angle can't exist there at all. */
export const HEADLINE_ANGLE_OPTIONS = ANGLE_OPTIONS.filter(angle => angle !== 'Founder');

export function buildStyleLibrary(checked: Record<string, boolean>): string {
  const lines = STYLE_LIBRARY.filter(style => checked[style.key] !== false).map(style => `- ${style.line}`);
  if (!lines.length) return '';
  const restricted = lines.length < STYLE_LIBRARY.length ? ' Use ONLY these styles.' : '';
  return `CAPTION STYLES — each primary text uses a DIFFERENT one; spread evenly if options outnumber styles.${restricted}
${lines.join('\n')}`;
}

export const DEFAULT_STUDIO_PROMPT = `You write Meta ad copy for Dosaze sleep products that compels qualified readers to convert. Prioritize qualified attention over broad attention: attract people who genuinely have the problem, not the most clicks.

PRODUCT: ${VAR_PRODUCT}

SELLING POINTS (the claims this creative carries):
${VAR_SELLING_POINTS}

OUR RECENT WINNING HEADLINES (account-wide Dosaze winners, proven by real spend and ROAS — study their structure, specificity and voice; some sell OTHER Dosaze products, so imitate the craft, never their product facts, claims or offers — everything you write sells the PRODUCT above):
${VAR_WINNING_HEADLINES}

OUR RECENT WINNING PRIMARY TEXTS (same rule — imitate the craft, not the product facts):
${VAR_WINNING_PRIMARY_TEXTS}

THE ORIGINAL AD THIS CREATIVE WAS MODELED ON:
Headline: ${VAR_ORIGINAL_HEADLINE}
Primary text: ${VAR_ORIGINAL_PRIMARY_TEXT}

WHY THE ORIGINAL WORKED (distilled analysis):
${VAR_ANALYSIS}

ADDITIONAL DIRECTION:
${VAR_EXTRA}

ALREADY GENERATED FOR THIS PRODUCT (earlier runs, including other creatives) — do NOT repeat or lightly rephrase any of these:
${VAR_PREVIOUS_COPY}

${VAR_HEADLINE_RULES}

${VAR_STYLE_LIBRARY}

${VAR_FORMAT_RULES}

OUTPUT — reply with ONLY this JSON, no fences, no commentary:
{"headlines": {"modeled": [{"text": "...", "angle": "..."}], "novel": [...]}, "primaryTexts": {"modeled": [{"text": "...", "style": "...", "angle": "..."}], "novel": [...]}}
- headlines: ${VAR_MODELED_COUNT} modeled CLOSELY on the winners + ${VAR_NOVEL_COUNT} genuinely NEW angles; primaryTexts: same counts. Lists are INDEPENDENT — Meta pairs them at delivery.
- "modeled" and "novel" are DISJOINT: exactly the stated counts in each, and no text may appear in both.
- Modeling on a winner NEVER overrides HEADLINE RULES or CAPTION FORMAT — carry the winner's angle and energy, obey the rules (a 77-char winner still yields a ≤40-char headline).
- "angle": Problem-Naming | Promise | Pattern Interrupt | Social Proof | Authority | Offer-Led | Sensory | Skeptic | Founder | Lifestyle. "style": the caption format used.
- Never repeat or rephrase the ALREADY GENERATED list — different openings, imagery, hooks.`;

/** Substitute variables; then prune section blocks whose value is empty:
 * a heading line whose following variable resolved to '' disappears with it. */
export function composePrompt(template: string, values: Record<string, string>): string {
  let out = template;
  for (const [key, value] of Object.entries(values)) {
    out = out.split(key).join(value);
  }
  // Prune: any paragraph (blank-line separated) that is now just a heading
  // with an empty body (ends with ':' or contains only labels) is dropped.
  const EMPTY_LABEL = /^(Headline|Primary text):\s*$/;
  return out
    .split('\n\n')
    .filter((block) => {
      const lines = block.split('\n').map(l => l.trim());
      const hasContent = lines.some(l => l && !/[:：]$/.test(l) && !EMPTY_LABEL.test(l));
      return hasContent;
    })
    // A kept block can still carry a value-less label (e.g. "Headline:" when
    // the source ad has none) — drop just that line, not the block.
    .map((block) => block.split('\n').filter(l => !EMPTY_LABEL.test(l.trim())).join('\n'))
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** chars/4 — a coarse but honest token gauge for the ingredient cards. */
export function estimateTokens(text: string): number {
  return Math.ceil((text || '').length / 4);
}

/** Marks the exact character a truncated response was cut at. The repair
 * appends it to the one string it had to close, so the incomplete item carries
 * proof of its own truncation through JSON.parse — and, because it is derived
 * from the stored raw text, through history replay too (the API's `truncated`
 * flag lives only on the response that paid for it). U+E000 is a private-use
 * code point: it cannot occur in model-written copy, and JSON allows it
 * unescaped inside a string. */
export const TRUNCATION_MARKER = '\uE000';

/** One suggestion with its optional metadata, from the decoupled-lists JSON. */
export interface SuggestionOption {
  text: string;
  angle?: string;
  style?: string;
  /** The response was cut off INSIDE this text — it is incomplete copy, and
   * must never be presented (or auto-applied) as a finished option. */
  truncated?: boolean;
}

export interface SuggestionSections {
  headlinesModeled: SuggestionOption[];
  headlinesNovel: SuggestionOption[];
  primaryModeled: SuggestionOption[];
  primaryNovel: SuggestionOption[];
  /** True when no structured format was recognized — show the raw text. */
  unparsed?: boolean;
  /** True when the response was cut off and only parsed after repair: items
   * are missing, and any item marked `truncated` is incomplete. */
  truncated?: boolean;
}

const EMPTY: SuggestionSections = { headlinesModeled: [], headlinesNovel: [], primaryModeled: [], primaryNovel: [] };

// Accepts both the typed items ({text, angle, style}) and plain strings.
const toOptions = (arr: unknown): SuggestionOption[] => (Array.isArray(arr) ? arr : [])
  .map((v: any): SuggestionOption | null => {
    const raw = typeof v === 'string' ? v : (v && typeof v.text === 'string' ? v.text : '');
    const cutAt = raw.indexOf(TRUNCATION_MARKER);
    const text = (cutAt === -1 ? raw : raw.slice(0, cutAt)).trim();
    if (!text) return null;
    const option: SuggestionOption = { text };
    if (v && typeof v.angle === 'string' && v.angle.trim()) option.angle = v.angle.trim();
    if (v && typeof v.style === 'string' && v.style.trim()) option.style = v.style.trim();
    if (cutAt !== -1) option.truncated = true;
    return option;
  })
  .filter((v): v is SuggestionOption => !!v);

/** Close what a truncated JSON response left open. The model slip we have
 * actually seen is stopping a closer short of the end (`}]}` instead of
 * `}]}}`); a harder cut can land mid-string or after a comma. Tracks string
 * state so braces inside copy text don't count; returns null when the text
 * has mismatched closers (malformed, not truncated) or nothing is missing.
 *
 * A string closed here was cut mid-text, so it gets TRUNCATION_MARKER before
 * its closing quote: the repair stays honest about which item is incomplete
 * instead of handing back a mid-word fragment that reads as finished copy. */
export function repairTruncatedJson(text: string): string | null {
  const stack: string[] = [];
  let inString = false;
  let escaped = false;
  for (const ch of text) {
    if (escaped) { escaped = false; continue; }
    if (inString) {
      if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === '{') stack.push('}');
    else if (ch === '[') stack.push(']');
    else if (ch === '}' || ch === ']') {
      if (stack.pop() !== ch) return null;
    }
  }
  if (!stack.length && !inString) return null;
  let body = text;
  if (inString) {
    if (escaped) body = body.slice(0, -1); // a lone trailing backslash would escape our closing quote
    body += `${TRUNCATION_MARKER}"`;
  }
  body = body.replace(/[\s,]+$/, '');

  // A cut can also leave a dangling KEY — `"style":` or a bare `"style"`
  // inside an object — which no closer can legally follow. Strip it back to
  // the previous complete member. openerOf finds a string's opening quote
  // (skipping escaped quotes) so keys containing escapes still resolve.
  const openerOf = (s: string, closeAt: number): number => {
    for (let i = closeAt - 1; i >= 0; i -= 1) {
      if (s[i] !== '"') continue;
      let backslashes = 0;
      while (i - 1 - backslashes >= 0 && s[i - 1 - backslashes] === '\\') backslashes += 1;
      if (backslashes % 2 === 0) return i;
    }
    return -1;
  };
  if (/:\s*$/.test(body)) {
    const beforeColon = body.replace(/:\s*$/, '').replace(/\s+$/, '');
    if (!beforeColon.endsWith('"')) return null;
    const open = openerOf(beforeColon, beforeColon.length - 1);
    if (open === -1) return null;
    body = beforeColon.slice(0, open).replace(/[\s,]+$/, '');
  } else if (stack[stack.length - 1] === '}' && body.endsWith('"')) {
    // A trailing string directly inside an object is a key only when it
    // follows `{` or `,`; after `:` it is a member value and stays.
    const open = openerOf(body, body.length - 1);
    if (open === -1) return null;
    const before = body.slice(0, open).replace(/\s+$/, '');
    if (before.endsWith('{') || before.endsWith(',')) {
      body = before.replace(/[\s,]+$/, '');
    }
  }
  return body + stack.reverse().join('');
}

/** The model sometimes restates novel items inside modeled and then repeats
 * them in novel (seen in production: all five headlines under modeled, the
 * two novel ones duplicated). The lists are disjoint by definition, so drop
 * duplicates — novel wins, because a text present in both is the one the
 * rotation assigned to novel. Headlines and primaries dedupe separately. */
function dedupeSections(sections: SuggestionSections): SuggestionSections {
  if (sections.unparsed) return sections;
  const key = (t: string) => t.trim().toLowerCase();
  const dedupePair = (novel: SuggestionOption[], modeled: SuggestionOption[]) => {
    const seen = new Set<string>();
    const take = (opts: SuggestionOption[]) => opts.filter(o => {
      const k = key(o.text);
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    const keptNovel = take(novel);
    return { novel: keptNovel, modeled: take(modeled) };
  };
  const h = dedupePair(sections.headlinesNovel, sections.headlinesModeled);
  const p = dedupePair(sections.primaryNovel, sections.primaryModeled);
  return {
    ...sections,
    headlinesModeled: h.modeled,
    headlinesNovel: h.novel,
    primaryModeled: p.modeled,
    primaryNovel: p.novel,
  };
}

/** Split a raw response into the four labeled lists. The one response
 * format is the decoupled-lists JSON, repaired when the model was cut off:
 * a repaired run is flagged `truncated`, and the item the cut landed inside
 * is flagged too. Anything else returns unparsed so the UI shows the raw
 * text — never invented, relabelled or silently dropped items. */
export function splitSuggestions(text: string): SuggestionSections {
  return dedupeSections(splitSuggestionsRaw(text));
}

function splitSuggestionsRaw(text: string): SuggestionSections {
  const src = (text || '').replace(/\r/g, '');
  const jStart = src.indexOf('{');
  const jEnd = src.lastIndexOf('}');
  if (jStart === -1) return { ...EMPTY, unparsed: true };

  let parsed: any = null;
  let truncated = false;
  if (jEnd > jStart) {
    try { parsed = JSON.parse(src.slice(jStart, jEnd + 1)); } catch { /* try repair below */ }
  }
  if (!parsed) {
    const repaired = repairTruncatedJson(src.slice(jStart));
    if (repaired !== null) {
      try {
        parsed = JSON.parse(repaired);
        truncated = true;
      } catch { /* genuinely malformed, not truncated */ }
    }
  }
  if (!parsed) return { ...EMPTY, unparsed: true };

  const out: SuggestionSections = {
    headlinesModeled: toOptions(parsed?.headlines?.modeled),
    headlinesNovel: toOptions(parsed?.headlines?.novel),
    primaryModeled: toOptions(parsed?.primaryTexts?.modeled),
    primaryNovel: toOptions(parsed?.primaryTexts?.novel),
  };
  if (truncated) out.truncated = true;
  if (out.headlinesModeled.length || out.headlinesNovel.length || out.primaryModeled.length || out.primaryNovel.length) return out;
  // Valid JSON in some other shape: nothing here is a suggestion list, so say
  // so and show the raw text rather than guessing what the keys meant.
  return { ...EMPTY, unparsed: true, truncated: truncated || undefined };
}
